import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext.jsx';
import { api } from '../api/client.js';
import { SOMATIC_CHECKIN_FEELINGS, ALINEA_PROFILES } from '../utils/constants.js';
import {
    getPerdonarseIntro,
    getPerdonarseOptionsForStep,
    getPerdonarseStepMeta,
    getPerdonarseSteps,
    mapPerdonarseToIncident,
    successMessageForValence,
    valenceFromFeelingKey,
    valenceFromSomaticFeeling,
} from '../data/epaPerdonarseOptions.js';

const EPA_NEEDS = [
    { key: 'descanso', emoji: '🧘', label: 'Pausa' },
    { key: 'agua', emoji: '💧', label: 'Agua' },
    { key: 'hablar_con_jefe', emoji: '🗣️', label: 'Apoyo' },
];

const ADAPT_ANCHORS = [
    { key: 'habilidad', emoji: '⚙️', label: 'Mi habilidad sigue' },
    { key: 'equipo', emoji: '🤝', label: 'El equipo cuenta' },
    { key: 'ritmo', emoji: '🌊', label: 'Ajusto el ritmo' },
];

function EpaIconRow({ options, selectedKey, onPick, disabled, cols = 3 }) {
    const colClass = cols === 3 ? 'wjo-epa-feel-row' : `wjo-epa-icon-row wjo-epa-icon-row-cols-${cols}`;
    return (
        <div className={`${colClass}${disabled ? ' wjo-epa-feel-row-busy' : ''}`} role="group">
            {options.map((opt) => (
                <button
                    key={opt.key}
                    type="button"
                    className={`wjo-epa-feel-btn${selectedKey === opt.key ? ' on' : ''}`}
                    disabled={disabled}
                    title={opt.label}
                    onClick={() => onPick(opt)}
                >
                    <span className="wjo-epa-feel-emoji" aria-hidden>
                        {opt.emoji}
                    </span>
                    <span className="wjo-epa-feel-label">{opt.label}</span>
                </button>
            ))}
        </div>
    );
}

function EpaSectionHead({ title, badge, badgeClass = '' }) {
    return (
        <div className="wjo-epa-escuchar-top">
            <h3 className="wjo-epa-escuchar-title">{title}</h3>
            <span className={`wjo-epa-badge${badgeClass ? ` ${badgeClass}` : ''}`}>{badge}</span>
        </div>
    );
}

/** Módulo EPA completo en columna jornada: E · P · A con iconos en fila y guardado al tocar. */
export default function WorkerEpaGuided() {
    const { user } = useAuth();
    const profile = ALINEA_PROFILES[user?.perfil_alinea] || ALINEA_PROFILES.constante;

    const [toast, setToast] = useState('');

    // E · Escucharse
    const [feeling, setFeeling] = useState('');
    const [need, setNeed] = useState('');
    const [showPause, setShowPause] = useState(false);
    const [pauseTimer, setPauseTimer] = useState(90);
    const [busyE, setBusyE] = useState(false);
    const [savedE, setSavedE] = useState('');

    // P · Perdonarse (ramas por valencia; hereda señal de Escucha)
    const [lastSomaticFeeling, setLastSomaticFeeling] = useState('');
    const [procesaExpanded, setProcesaExpanded] = useState(true);
    const [perdonarStep, setPerdonarStep] = useState(0);
    const [perdonarFeeling, setPerdonarFeeling] = useState('');
    const [perdonarContext, setPerdonarContext] = useState('');
    const [perdonarTakeaway, setPerdonarTakeaway] = useState('');
    const [perdonarAffirm, setPerdonarAffirm] = useState('');
    const [perdonarDone, setPerdonarDone] = useState(false);
    const [busyP, setBusyP] = useState(false);
    const [lessons, setLessons] = useState([]);

    // A · Adaptarse
    const [adaptKey, setAdaptKey] = useState('');
    const [busyA, setBusyA] = useState(false);
    const [savedA, setSavedA] = useState('');

    useEffect(() => {
        api.getLessons()
            .then((data) => setLessons(Array.isArray(data) ? data : []))
            .catch(() => setLessons([]));
    }, []);

    useEffect(() => {
        if (!showPause || pauseTimer <= 0) return undefined;
        const t = window.setInterval(() => setPauseTimer((n) => n - 1), 1000);
        return () => window.clearInterval(t);
    }, [showPause, pauseTimer]);

    function flash(msg) {
        setToast(msg);
        window.setTimeout(() => setToast(''), 3000);
    }

    function flashSaved(setter) {
        setter('Guardado ✓');
        window.setTimeout(() => setter(''), 2200);
    }

    async function submitCheckin(feelingVal, needVal) {
        setBusyE(true);
        setSavedE('');
        try {
            const data = await api.submitCheckin({ feeling: feelingVal, need: needVal || null });
            if (data?.showPause) {
                setShowPause(true);
                setPauseTimer(90);
            } else {
                flashSaved(setSavedE);
                setFeeling('');
                setNeed('');
            }
        } catch (err) {
            flash(err.message || 'No se pudo guardar');
            setFeeling('');
            setNeed('');
        } finally {
            setBusyE(false);
        }
    }

    async function onPickFeeling(val) {
        if (busyE) return;
        setFeeling(val);
        setLastSomaticFeeling(val);
        setSavedE('');
        // Si está en eje, Procesa queda opcional (colapsado); con carga/fricción se abre.
        const band = valenceFromSomaticFeeling(val);
        setProcesaExpanded(band !== 'eje');
        if (val === 'mal') {
            setNeed('');
            return;
        }
        setNeed('');
        await submitCheckin(val, null);
    }

    async function onPickNeed(key) {
        if (busyE) return;
        setFeeling('mal');
        setNeed(key);
        await submitCheckin('mal', key);
    }

    async function finishPause() {
        try {
            await api.recordPause();
            setShowPause(false);
            flash('Pausa registrada.');
            setFeeling('');
            setNeed('');
            flashSaved(setSavedE);
        } catch (err) {
            flash(err.message || 'Error');
        }
    }

    const escucharFeelings = Object.entries(SOMATIC_CHECKIN_FEELINGS).map(([key, opt]) => ({
        key,
        emoji: opt.emoji,
        label: opt.label,
    }));

    function selectPerdonarse(stepIndex, opt) {
        if (busyP) return;
        setPerdonarAffirm(opt.affirm || '');
        const nextFeeling = stepIndex === 0 ? opt.key : perdonarFeeling;
        const nextContext = stepIndex === 1 ? opt.key : perdonarContext;
        if (stepIndex === 0) {
            setPerdonarFeeling(opt.key);
            setPerdonarContext('');
            setPerdonarTakeaway('');
        }
        if (stepIndex === 1) setPerdonarContext(opt.key);
        if (stepIndex === 2) setPerdonarTakeaway(opt.key);

        window.setTimeout(async () => {
            setPerdonarAffirm('');
            if (stepIndex < 2) {
                setPerdonarStep(stepIndex + 1);
                return;
            }
            setBusyP(true);
            try {
                const fullPayload = mapPerdonarseToIncident({
                    feelingKey: nextFeeling,
                    contextKey: nextContext,
                    takeawayKey: opt.key,
                });
                const data = await api.submitIncident(fullPayload);
                flash(data?.message || successMessageForValence(fullPayload.valence));
                setPerdonarDone(true);
                const updated = await api.getLessons();
                setLessons(Array.isArray(updated) ? updated : []);
            } catch (err) {
                flash(err.message || 'No se pudo guardar');
            } finally {
                setBusyP(false);
            }
        }, 550);
    }

    function resetPerdonarse() {
        setPerdonarStep(0);
        setPerdonarFeeling('');
        setPerdonarContext('');
        setPerdonarTakeaway('');
        setPerdonarAffirm('');
        setPerdonarDone(false);
        setProcesaExpanded(true);
    }

    async function onPickAdapt(opt) {
        if (busyA) return;
        setAdaptKey(opt.key);
        setBusyA(true);
        setSavedA('');
        try {
            await api.submitIncident({
                whatHappened: `Anclaje: ${opt.label}`,
                whyItHappened: 'Reflexión personal frente al cambio en planta',
                lessonLearned: 'Ejercicio de anclaje en turno',
                isAnonymous: true,
            });
            flashSaved(setSavedA);
            window.setTimeout(() => setAdaptKey(''), 2200);
        } catch (err) {
            flash(err.message || 'No se pudo guardar');
            setAdaptKey('');
        } finally {
            setBusyA(false);
        }
    }

    const perdonarValence = perdonarFeeling ? valenceFromFeelingKey(perdonarFeeling) : null;
    const perdonarSteps = getPerdonarseSteps(perdonarValence || 'friccion');
    const perdonarStepMeta = getPerdonarseStepMeta(perdonarStep, perdonarFeeling);
    const perdonarIntro = getPerdonarseIntro(lastSomaticFeeling);
    const perdonarOptions = getPerdonarseOptionsForStep(perdonarStep, perdonarFeeling);
    const perdonarSelected =
        perdonarStep === 0 ? perdonarFeeling : perdonarStep === 1 ? perdonarContext : perdonarTakeaway;

    return (
        <div className="wjo-epa-panel">
            <header className="wjo-epa-panel-head wjo-epa-panel-head-compact">
                <p className="wjo-epa-panel-kicker">EPA · Escucha · Procesa · Ajusta</p>
                <h2 className="wjo-epa-panel-title">Pausas y recursos</h2>
                {profile.daily_anchor && (
                    <p className="wjo-epa-anchor-mini">
                        <span aria-hidden>{profile.emoji}</span> {profile.daily_anchor}
                    </p>
                )}
            </header>

            {/* E · Escucharse */}
            <section className="wjo-epa-block" aria-label="Escucha">
                <EpaSectionHead title="Escucha" badge="E · Sintonía" />
                {showPause ? (
                    <div className="wjo-epa-pause-inline">
                        <p className="wjo-epa-pause-timer" aria-live="polite">
                            {Math.floor(pauseTimer / 60)}:{String(pauseTimer % 60).padStart(2, '0')}
                        </p>
                        <p className="wjo-epa-panel-hint wjo-epa-panel-hint-tight">Pausa consciente. Respira profundo.</p>
                        {pauseTimer <= 0 && (
                            <button type="button" className="wjo-epa-submit" onClick={finishPause}>
                                Terminé la pausa
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        <p className="wjo-epa-panel-hint wjo-epa-panel-hint-tight">
                            ¿Cómo está tu cuerpo ahora? Toca una opción; se guarda sola.
                        </p>
                        <EpaIconRow
                            options={escucharFeelings}
                            selectedKey={feeling}
                            onPick={(opt) => onPickFeeling(opt.key)}
                            disabled={busyE}
                            cols={3}
                        />
                        {feeling === 'mal' && !busyE && (
                            <div className="wjo-epa-needs-block wjo-epa-needs-block-compact">
                                <p className="wjo-epa-needs-title">¿Qué te ayudaría?</p>
                                <EpaIconRow
                                    options={EPA_NEEDS}
                                    selectedKey={need}
                                    onPick={(opt) => onPickNeed(opt.key)}
                                    disabled={busyE}
                                    cols={3}
                                />
                            </div>
                        )}
                        {busyE ? <p className="wjo-epa-saving">Guardando…</p> : null}
                        {savedE ? <p className="wjo-epa-saved">{savedE}</p> : null}
                    </>
                )}
            </section>

            {/* P · Perdonarse */}
            <section className="wjo-epa-block" aria-label="Procesa">
                <EpaSectionHead title="Procesa" badge="P · Evolución" badgeClass="wjo-epa-badge-p" />
                {perdonarDone ? (
                    <div className="wjo-epa-done-mini">
                        <p className="wjo-epa-saved">Registrado con respeto ✓</p>
                        <p className="wjo-epa-panel-hint wjo-epa-panel-hint-tight">
                            Anónimo para ti; no es reporte contra nadie.
                        </p>
                        <button type="button" className="wjo-epa-link-btn" onClick={resetPerdonarse}>
                            Otro registro
                        </button>
                    </div>
                ) : !procesaExpanded ? (
                    <div className="wjo-epa-done-mini">
                        <p className="wjo-epa-panel-hint wjo-epa-panel-hint-tight">{perdonarIntro.body}</p>
                        <button type="button" className="wjo-epa-link-btn" onClick={() => setProcesaExpanded(true)}>
                            Abrir Procesa (opcional)
                        </button>
                    </div>
                ) : (
                    <>
                        {perdonarStep === 0 && (
                            <p className="wjo-epa-panel-hint wjo-epa-panel-hint-tight">{perdonarIntro.body}</p>
                        )}
                        {perdonarValence ? (
                            <p className="wjo-epa-panel-hint wjo-epa-panel-hint-tight" aria-live="polite">
                                {perdonarValence === 'eje'
                                    ? 'Rama: sostén (sin buscar problema).'
                                    : perdonarValence === 'carga'
                                      ? 'Rama: carga del día.'
                                      : 'Rama: momento de fricción.'}
                            </p>
                        ) : null}
                        <div className="wjo-epa-guided-progress" aria-label="Pasos Procesa">
                            {perdonarSteps.map((s, i) => (
                                <span
                                    key={s.key}
                                    className={`wjo-epa-guided-dot${i === perdonarStep ? ' active' : ''}${i < perdonarStep ? ' done' : ''}`}
                                />
                            ))}
                        </div>
                        <p className="wjo-epa-step-q">{perdonarStepMeta.title}</p>
                        {perdonarAffirm ? (
                            <p className="wjo-epa-guided-affirm" role="status">
                                {perdonarAffirm}
                            </p>
                        ) : null}
                        <div className="wjo-epa-scroll-row-wrap">
                            <EpaIconRow
                                options={perdonarOptions}
                                selectedKey={perdonarSelected}
                                onPick={(opt) => selectPerdonarse(perdonarStep, opt)}
                                disabled={busyP}
                                cols={perdonarOptions.length <= 6 ? 3 : 4}
                            />
                        </div>
                        {perdonarStep > 0 && (
                            <button
                                type="button"
                                className="wjo-epa-link-btn"
                                disabled={busyP}
                                onClick={() => {
                                    setPerdonarAffirm('');
                                    setPerdonarStep((s) => Math.max(0, s - 1));
                                }}
                            >
                                ← Paso anterior
                            </button>
                        )}
                        {busyP ? <p className="wjo-epa-saving">Guardando…</p> : null}
                    </>
                )}
                {lessons.length > 0 && (
                    <div className="wjo-epa-lessons">
                        <p className="wjo-epa-needs-title">Aprendizajes del equipo (anónimos)</p>
                        <ul className="wjo-epa-lessons-list">
                            {lessons.slice(0, 2).map((l, i) => (
                                <li key={i}>{l.lesson_learned || l.what_happened}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </section>

            {/* A · Adaptarse */}
            <section className="wjo-epa-block" aria-label="Ajusta">
                <EpaSectionHead title="Ajusta" badge="A · Fluidez" badgeClass="wjo-epa-badge-a" />
                <p className="wjo-epa-adapt-quote">El cambio no te quita lo que sabes. Te da algo nuevo que aprender.</p>
                <p className="wjo-epa-panel-hint wjo-epa-panel-hint-tight">
                    ¿Qué sigue igual en ti frente al cambio? Toca una; se guarda sola.
                </p>
                <EpaIconRow
                    options={ADAPT_ANCHORS}
                    selectedKey={adaptKey}
                    onPick={onPickAdapt}
                    disabled={busyA}
                    cols={3}
                />
                {busyA ? <p className="wjo-epa-saving">Guardando…</p> : null}
                {savedA ? <p className="wjo-epa-saved">{savedA}</p> : null}
            </section>

            {toast ? <p className="wjo-epa-toast">{toast}</p> : null}
        </div>
    );
}
